/**
 * EanCameraScanner (Jun-2026, User-Wunsch) — leichter Kamera-EAN/Barcode-Scanner.
 *
 * Im Gegensatz zum WMS-`BarcodeScannerModal` bucht dieser NICHTS — er liefert nur
 * den erkannten Code einmalig via `onDetected(code)` zurück (z. B. für die
 * Direktverkauf-Position via Handy-Kamera). Nutzt @zxing/browser.
 *
 * - Erkennt EAN-13, EAN-8 (inkl. interner Haus-EAN-8), UPC-A/E, Code128/39, ITF & QR.
 * - Nutzt standardmäßig IMMER die Rückkamera (facingMode: environment), nie die
 *   Frontkamera. Über „Andere Kamera" kann bei Bedarf manuell durchgeschaltet werden.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { BrowserMultiFormatReader } from "@zxing/browser";
import { DecodeHintType, BarcodeFormat } from "@zxing/library";
import { X, Camera, SwitchCamera } from "lucide-react";

const HINTS = new Map();
HINTS.set(DecodeHintType.POSSIBLE_FORMATS, [
    BarcodeFormat.EAN_13,
    BarcodeFormat.EAN_8,
    BarcodeFormat.UPC_A,
    BarcodeFormat.UPC_E,
    BarcodeFormat.CODE_128,
    BarcodeFormat.CODE_39,
    BarcodeFormat.ITF,
    BarcodeFormat.QR_CODE,
    BarcodeFormat.DATA_MATRIX,
]);
HINTS.set(DecodeHintType.TRY_HARDER, true);

const isRear = (label = "") => /back|rear|environment|rück|ruck|world|hinten/i.test(label);
const isFront = (label = "") => /front|face|user|selfie|vorne/i.test(label);

export default function EanCameraScanner({ onDetected, onClose }) {
    const videoRef = useRef(null);
    const controlsRef = useRef(null);
    const errRef = useRef(null);
    const [devices, setDevices] = useState([]);
    const [deviceId, setDeviceId] = useState(null); // null = noch nicht gewählt
    const [ready, setReady] = useState(false);

    // 1) Geräte auflisten (nach Berechtigung), Rückkamera vorwählen, Front ausschließen.
    useEffect(() => {
        let active = true;
        (async () => {
            try {
                try {
                    const warmup = await navigator.mediaDevices.getUserMedia({
                        video: { facingMode: { ideal: "environment" } },
                    });
                    warmup.getTracks().forEach((t) => t.stop());
                } catch { /* Berechtigung wird ggf. später erneut angefragt */ }
                const list = await BrowserMultiFormatReader.listVideoInputDevices();
                if (!active) return;
                setDevices(list);
                const rear = list.find((d) => isRear(d.label));
                const nonFront = list.find((d) => !isFront(d.label));
                setDeviceId((rear || nonFront || list[0])?.deviceId || "");
            } catch (e) {
                if (errRef.current) errRef.current.textContent = "Kamera-Zugriff fehlgeschlagen: " + (e?.message || "unbekannt");
            }
        })();
        return () => { active = false; };
    }, []);

    // 2) Scan starten/neustarten sobald ein Gerät gewählt ist.
    useEffect(() => {
        if (deviceId === null) return undefined; // Geräteliste noch nicht bereit
        const reader = new BrowserMultiFormatReader(HINTS);
        let active = true;
        setReady(false);
        (async () => {
            try {
                if (!videoRef.current) return;
                const onResult = (result) => {
                    if (!active || !result) return;
                    active = false;
                    try { controlsRef.current?.stop(); } catch { /* noop */ }
                    onDetected(result.getText());
                };
                let controls;
                if (deviceId) {
                    controls = await reader.decodeFromVideoDevice(deviceId, videoRef.current, onResult);
                } else {
                    // Fallback ohne Labels (z. B. iOS vor Freigabe) → facingMode erzwingt hinten.
                    controls = await reader.decodeFromConstraints(
                        { video: { facingMode: { ideal: "environment" } } },
                        videoRef.current,
                        onResult,
                    );
                }
                controlsRef.current = controls;
                if (active) setReady(true);
            } catch (e) {
                if (errRef.current) errRef.current.textContent = "Kamera-Zugriff fehlgeschlagen: " + (e?.message || "unbekannt");
            }
        })();
        return () => { active = false; try { controlsRef.current?.stop(); } catch { /* noop */ } };
    }, [deviceId, onDetected]);

    // Manueller Kamera-Wechsel (Sicherheitsnetz bei falsch gelabelten Geräten).
    const switchCamera = useCallback(() => {
        if (devices.length < 2) return;
        try { controlsRef.current?.stop(); } catch { /* noop */ }
        setDeviceId((cur) => {
            const idx = devices.findIndex((d) => d.deviceId === cur);
            const next = devices[(idx + 1) % devices.length];
            return next?.deviceId || cur;
        });
    }, [devices]);

    const currentLabel = devices.find((d) => d.deviceId === deviceId)?.label || "";

    return (
        <div className="fixed inset-0 z-[70] bg-black/85 flex items-center justify-center p-3 sm:p-4" data-testid="ean-camera-scanner">
            <div className="bg-white rounded-sm w-full max-w-md overflow-hidden shadow-xl max-h-[92vh] flex flex-col">
                <div className="flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 bg-[#111827] text-white shrink-0">
                    <span className="text-sm font-bold uppercase tracking-wider flex items-center gap-2"><Camera className="w-5 h-5 text-[#FFCC00]" /> EAN / Barcode scannen</span>
                    <div className="flex items-center gap-1">
                        {devices.length > 1 && (
                            <button type="button" onClick={switchCamera} data-testid="ean-camera-switch"
                                aria-label="Andere Kamera" title="Andere Kamera"
                                className="p-2 rounded-sm hover:bg-white/10 inline-flex items-center gap-1 text-xs font-bold">
                                <SwitchCamera className="w-5 h-5" />
                            </button>
                        )}
                        <button type="button" onClick={onClose} data-testid="ean-camera-close" aria-label="Schließen" className="p-2 rounded-sm hover:bg-white/10"><X className="w-7 h-7 sm:w-5 sm:h-5" /></button>
                    </div>
                </div>
                {/* Jul-2026 (User-Wunsch) — Kamera-Ansicht zentriert & kompakt:
                    feste Ratio statt Vollbild, Video füllt den Rahmen mittig (object-cover). */}
                <div className="bg-black relative aspect-[3/4] max-h-[55vh] sm:aspect-video sm:max-h-none w-full">
                    <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />
                    <div className="absolute inset-x-8 top-1/2 -translate-y-1/2 h-0.5 bg-[#FFCC00]/80 animate-pulse" />
                    {devices.length > 1 && (
                        <button type="button" onClick={switchCamera} data-testid="ean-camera-switch-overlay"
                            className="absolute bottom-2 right-2 bg-black/80 hover:bg-black/80 text-white text-xs font-bold rounded-sm px-2.5 py-1.5 inline-flex items-center gap-1.5">
                            <SwitchCamera className="w-4 h-4" /> Andere Kamera
                        </button>
                    )}
                </div>
                <p ref={errRef} className="text-xs text-gray-600 p-2.5 sm:p-3 shrink-0" data-testid="ean-camera-hint">
                    Kamera auf den Barcode/EAN richten — EAN-13, EAN-8, UPC, Code128 &amp; QR werden automatisch erkannt.
                    {currentLabel ? ` · Kamera: ${currentLabel}` : ""}
                </p>
            </div>
        </div>
    );
}
